import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  bulkDeleteFlows,
  bulkFlowUpdate,
  createFlow,
  deleteFlow,
  fetchFlow,
  fetchFlowFiltersTojsonAPI,
  fetchFlows,
  fetchFlowsMetadata,
  fetchFlowsPageSize,
  reconciliations,
  reconciliationsForced,
  sendMailReminder,
  splitPayment,
  updateFlow,
} from '~/api/flow';
import { queryClient } from '~/client/queryClient';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import { useExcluded, useResetSelection, useSelected } from '~/state/selection';
import { useFilterState } from '~/state/tableFilters';
import { useSorting } from '~/state/tableSorting';
import { CashflowDataDirection } from '~/types/cashflow';
import { DocumentDirection, DocumentQueryKeys, DocumentStatus, IssuableDocumentTypes } from '~/types/document';
import {
  BulkUpdateFlowPayload,
  DownloadXMLBody,
  FetchFlowFilters,
  Flow,
  FlowCreate,
  FlowSorting,
  FlowsFiltersFields,
  FlowsQueryKeys,
  FlowWithDocument,
  UpdateFlowValues,
} from '~/types/flow';
import { OutstandingDataDirection, OutstandingKeys } from '~/types/outstanding';
import { ReconciliationsQueryKeys } from '~/types/reconciliation';
import { assertIsDefined, isDefined } from '~/types/typeGuards';
import { downloadExcel, downloadXML } from '~/utils/download';
import { formatDateForAPI } from '~/utils/formatDate';
import { buildJsonAPIQueryParams, SortInfo } from '~/utils/jsonapi-query';
import { useOustandingRouteType } from '~/views/outstanding/routing/useOutstandingRouteType';
import { AnalyticsEvents } from './analytics';
import { decorateWithReconciliations } from './reconciliation';

const invalidateFlowsCache = () => {
  queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: FlowsQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: OutstandingKeys.all });
  queryClient.invalidateQueries({ queryKey: ReconciliationsQueryKeys.all });
};

const fetchFlowWithReconciliation = async (id: Flow['id']): Promise<FlowWithDocument> => {
  const flow = await fetchFlow(id);
  const result = await decorateWithReconciliations([flow], ['transaction']);
  return result[0]!;
};

const useDocumentDirectionByRoute = () => {
  const routeType = useOustandingRouteType();
  if (routeType === OutstandingDataDirection.Outflow) return DocumentDirection.received;
  if (routeType === OutstandingDataDirection.Inflow) return DocumentDirection.issued;
  return null;
};

export const useFlowsTableQueryFilters = (): FetchFlowFilters => {
  const filters = useFilterState<FlowsFiltersFields>();
  const companyId = useActiveCompanyId();

  const { documentType, documentDirection, paymentStatus, ...others } = filters;
  const routeDocumentDirection = useDocumentDirectionByRoute();
  const isDraftStatus = paymentStatus === DocumentStatus.Draft;

  return {
    companyId,
    ...others,
    isInflow: filters.isInflow === '' ? null : filters.isInflow,
    documentDirection: documentDirection ?? routeDocumentDirection,
    documentType: documentType.length === 0 ? IssuableDocumentTypes : documentType,
    documentStatus: isDraftStatus ? DocumentStatus.Draft : null,
    paymentStatus: isDraftStatus ? null : paymentStatus,
  };
};

export const useFlowsTableInfiniteQuery = () => {
  const filters = useFlowsTableQueryFilters();
  const sorting = useSorting<FlowSorting>();

  return useFlowsInfiniteQuery(filters, sorting);
};

export const useFlowsInfiniteQuery = (
  filters: FetchFlowFilters,
  sorting: SortInfo<FlowSorting> = { sort: 'expectedPaymentDate', direction: 'desc' },
) => {
  const companyId = useActiveCompanyId();

  return useInfiniteQuery({
    enabled: isDefined(companyId),
    queryKey: FlowsQueryKeys.search(companyId, filters, sorting),
    queryFn: ({ pageParam }) => fetchFlows({ companyId, ...filters }, sorting, { cursor: pageParam }),
    initialPageParam: '',
    getNextPageParam: ({ meta, data }) => {
      if (data.length >= fetchFlowsPageSize) {
        return meta.page.cursor;
      }
      return undefined;
    },
    placeholderData: keepPreviousData,
    structuralSharing: false,
  });
};

export const useFlowsPageQueryOptions = (
  filters: FetchFlowFilters,
  sorting: SortInfo<FlowSorting>,
  pageSize: number,
) => {
  const companyId = useActiveCompanyId();

  return queryOptions({
    queryKey: FlowsQueryKeys.page(companyId, filters, sorting, pageSize),
    queryFn: () => {
      return fetchFlows({ ...filters, companyId }, sorting, { size: pageSize });
    },
    structuralSharing: false,
  });
};

export const useFlowsTableMetadata = () => {
  const filters = useFlowsTableQueryFilters();
  return useFlowsMetadata(filters);
};

export const useFlowsMetadata = (filters: FetchFlowFilters) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    ...useFlowsMetadataQueryOptions(filters),
    enabled: !!companyId,
  });
};

export const useFlowsMetadataQueryOptions = (filters: FetchFlowFilters) => {
  const companyId = useActiveCompanyId();

  return queryOptions({
    queryKey: FlowsQueryKeys.searchMetadata(companyId, filters),
    queryFn: () => fetchFlowsMetadata(filters),
    structuralSharing: false,
  });
};

interface UseUpdateFlowVariables {
  id: Flow['id'];
  payloadData: Partial<UpdateFlowValues>;
  updatedAttributes: string[];
  flowDirection: CashflowDataDirection;
}

export const useUpdateFlow = (context?: string) => {
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: (vars: UseUpdateFlowVariables) => updateFlow({ id: vars.id, payloadData: vars.payloadData }),
    onMutate: (newData) => {
      queryClient.setQueryData<Flow>(['Flows', newData.id], (flow) => {
        if (flow) {
          return {
            ...flow,
            paymentStatus: newData.payloadData.paymentStatus ?? flow.paymentStatus,
            expectedPaymentDate: newData.payloadData.expectedPaymentDate
              ? formatDateForAPI(newData.payloadData.expectedPaymentDate)
              : flow.expectedPaymentDate,
            account: newData.payloadData.account ?? flow.account,
          };
        }
      });
    },
    onSettled: () => {
      invalidateFlowsCache();
    },
    onSuccess: (data, variables) => {
      track(AnalyticsEvents.FLOW_UPDATED, {
        ...(context && { context }),
        flowId: data.id,
        updatedAttributes: variables.updatedAttributes.join(', '),
        flowDirection: variables.flowDirection,
      });
    },
  });
};

export const useCreateFlow = () => {
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: (values: FlowCreate) => createFlow(values, companyId),
    onSuccess() {
      invalidateFlowsCache();
    },
  });
};

const exportFlows = async (filter: FetchFlowFilters) => {
  const filters = fetchFlowFiltersTojsonAPI(filter);
  const queryParams = buildJsonAPIQueryParams(filters);
  await downloadExcel('/api/v1/outstanding/export', queryParams, 'scadenze.xlsx');
};

export const useExportFlows = () => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const filters = useFlowsTableQueryFilters();
  const selected = useSelected();
  const excluded = useExcluded();

  return useMutation({
    mutationFn: () => {
      const exportFilters: FetchFlowFilters = {
        ids: selected.length > 0 ? selected : undefined,
        excludeIds: excluded.length > 0 ? excluded : undefined,
        ...filters,
      };

      return exportFlows(exportFilters);
    },
    onError: () => snack.error(t('ERROR_EXPORTING')),
    onSettled: () => track(AnalyticsEvents.TRANSACTIONS_EXPORTED),
  });
};

const exportFlowsXML = async (body: DownloadXMLBody) => {
  // No more query params - all data is in the body
  await downloadXML('/api/v1/outstanding/export-xml', {}, body, 'scadenze.xml');
};

export const useExportFlowsXML = () => {
  const { t } = useTranslation();
  const { t: tOutstanding } = useTranslation('outstanding');

  return useMutation({
    mutationFn: ({
      accountId,
      companyId,
      sepaInitiatingPartyIdentifier,
      groupByCounterpart,
      fast,
      paymentDate,
      flows,
    }: {
      snackKey: string;
      accountId: string;
      companyId: string;
      sepaInitiatingPartyIdentifier?: string;
      groupByCounterpart: boolean;
      fast?: boolean;
      paymentDate?: string;
      flows: {
        id: string;
        paymentAccount: string;
        counterpartName: string;
        documentNumber: string;
        remittanceMessage?: string;
      }[];
    }) => {
      const body: DownloadXMLBody = {
        accountId,
        companyId,
        ...(sepaInitiatingPartyIdentifier && { sepaInitiatingPartyIdentifier }),
        groupByCounterpart,
        fast,
        ...(!fast && paymentDate && { paymentDate }),
        flows,
      };

      return exportFlowsXML(body);
    },
    onError: (_error, { snackKey }) => {
      snack.close(snackKey);
      snack.error(t('ERROR_EXPORTING'));
    },
    onSuccess: (_, { snackKey }) => {
      snack.close(snackKey);
      snack.enqueue(
        { type: 'success', message: tOutstanding('aside.download_xml_dialog.generated_xml') },
        { timeout: 1000 },
      );
    },
  });
};

export const useExportOutstandingFlows = () => {
  const { t } = useTranslation();
  const { track } = useAnalytics();

  const filters = useFlowsTableQueryFilters();

  return useMutation({
    mutationFn: () => {
      return exportFlows(filters);
    },
    onError: () => snack.error(t('ERROR_EXPORTING')),
    onSettled: () => track(AnalyticsEvents.TRANSACTIONS_EXPORTED),
  });
};

export const useDeleteFlow = () => {
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: deleteFlow,
    onSuccess: () => {
      track(AnalyticsEvents.FLOW_DELETED);
      invalidateFlowsCache();
    },
  });
};

export const useFlowQuery = (id: Flow['id'] | null): UseQueryResult<FlowWithDocument> => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: FlowsQueryKeys.single(id),
    queryFn: () => {
      assertIsDefined(companyId, 'company id must be defined');
      return fetchFlowWithReconciliation(id ?? '');
    },
    enabled: !!id,
    // Mutations invalidate this cache explicitly; a stale window avoids
    // refetching the flow + its reconciliations on every remount.
    staleTime: 30_000,
    structuralSharing: false,
  });
};

export const useFlowSuspenseQuery = (id: Flow['id'] | null) => {
  const companyId = useActiveCompanyId();
  return useSuspenseQuery({
    queryKey: FlowsQueryKeys.single(id ?? ''),
    queryFn: () => {
      assertIsDefined(companyId, 'company id must be defined');
      return isDefined(id) ? fetchFlowWithReconciliation(id) : null;
    },
    structuralSharing: false,
  });
};

export const useBulkUpdateFlows = () => {
  const { track } = useAnalytics();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: ({
      payload,
      filters,
    }: {
      payload: BulkUpdateFlowPayload;
      filters: Omit<FetchFlowFilters, 'companyId'>;
    }) => {
      return bulkFlowUpdate(payload, { ...filters, companyId });
    },

    onSuccess: (_data, vars) => {
      const updatedAttribute =
        'paymentStatus' in vars ? 'paymentStatus' : 'accountId' in vars ? 'accountId' : 'expectedPaymentDate';

      invalidateFlowsCache();
      track(AnalyticsEvents.FLOWS_BULK_UPDATED, { updatedAttribute, context: '/outstanding' });
    },
  });
};

export const useBulkUpdateFlowsTable = () => {
  const filters = useFlowsTableQueryFilters();
  const selected = useSelected();
  const excluded = useExcluded();
  const resetSelection = useResetSelection();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: (payload: BulkUpdateFlowPayload) => {
      const bulkFilters: FetchFlowFilters = {
        ids: selected.length > 0 ? selected : undefined,
        excludeIds: excluded.length > 0 ? excluded : undefined,
        ...filters,
      };
      return bulkFlowUpdate(payload, bulkFilters);
    },

    onSuccess: (_data, vars) => {
      const updatedAttribute = 'paymentStatus' in vars ? 'paymentStatus' : 'accountId';

      invalidateFlowsCache();
      resetSelection();
      track(AnalyticsEvents.FLOWS_BULK_UPDATED, { updatedAttribute, context: '/outstanding' });
    },
  });
};

export const useBulkDeleteFlowsTable = () => {
  const filters = useFlowsTableQueryFilters();
  const selected = useSelected();
  const excluded = useExcluded();
  const resetSelection = useResetSelection();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: () => {
      const bulkFilters: FetchFlowFilters = {
        ids: selected.length > 0 ? selected : undefined,
        excludeIds: excluded.length > 0 ? excluded : undefined,
        ...filters,
      };
      return bulkDeleteFlows(bulkFilters);
    },
    onSuccess: () => {
      invalidateFlowsCache();
      resetSelection();
      track(AnalyticsEvents.FLOWS_BULK_DELETED, { flowIds: selected });
    },
  });
};

export const useSendReminder = () => {
  return useMutation({
    mutationFn: sendMailReminder,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: FlowsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
    },
  });
};

export const useSplitPayment = (options?: { skipCacheInvalidation: boolean }) => {
  return useMutation({
    mutationFn: splitPayment,
    onSuccess() {
      if (options?.skipCacheInvalidation) return;
      queryClient.invalidateQueries({ queryKey: FlowsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
    },
  });
};

export const useFlowReconciliation = (flowId: Flow['id']) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryFn: async () => (await reconciliations.fetch(flowId)).transactionIds,
    queryKey: FlowsQueryKeys.reconciliations(companyId || '', flowId),
    enabled: Boolean(companyId),
    refetchOnMount: false,
    refetchInterval: 300_000,
  });
};

export const useFlowReconciliationForced = (flowId: Flow['id']) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryFn: async () => (await reconciliationsForced.fetch(flowId)).transactionIds,
    queryKey: FlowsQueryKeys.reconciliationsForced(companyId || '', flowId),
    enabled: Boolean(companyId),
  });
};
